import { DOCUMENT } from '@angular/common';
import { appearanceFavicon } from './appearance-favicon';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { API_BASE_URL } from '@core/api/api.tokens';
import { RolesService } from '@core/api/roles/roles.service';
import { AuthService } from '@core/auth/auth.service';
import { AuthUtils } from '@core/auth/auth.utils';
import { suppressErrorToast } from '@core/errors/error.http-context';
import { UserService } from '@core/user/user.service';
import { UserPreferencesService } from '@core/preferences/user-preferences.service';
import { FuseConfigService } from '@fuse/services/config';
import { catchError, forkJoin, map, of, shareReplay, switchMap } from 'rxjs';
import {
    Appearance,
    Billing,
    BrandScheme,
    contrastInk,
    inkOn,
    mix,
    normalizeAppearance,
    rgb,
} from './appearance.model';
@Injectable({ providedIn: 'root' })
export class AppearanceService {
    readonly preferences = inject(UserPreferencesService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly config = inject(FuseConfigService);
    private readonly users = inject(UserService);
    private readonly auth = inject(AuthService);
    private readonly roles = inject(RolesService);
    private readonly http = inject(HttpClient);
    private readonly api = inject(API_BASE_URL).replace(/\/+$/, '');
    private tenantKey = '';
    private dark = false;
    readonly appearance = signal<Appearance>(normalizeAppearance(this.read('mzeit.brand.boot.v1', {})));
    readonly billing = signal<Billing>({ companyName: '', street: '', zipCode: '', city: '' });
    readonly billingUnavailable = signal(false);
    readonly canCustomize = signal(false);
    readonly displayName = computed(() => this.appearance().displayName || this.billing().companyName || '—');
    readonly access$ = this.users.user$.pipe(
        switchMap((user) => {
            this.tenantKey = 'mzeit.appearance.v1:' + user.tenantId;
            this.canCustomize.set(false);
            this.appearance.set(normalizeAppearance(this.read(this.tenantKey, {})));
            this.apply();
            this.billing.set({ companyName: '', street: '', zipCode: '', city: '' });
            let roleId = '';
            try {
                roleId = AuthUtils.decodeToken(this.auth.accessToken)?.roleid || '';
            } catch {}
            return forkJoin({
                roles: this.roles.getRoles().pipe(catchError(() => of([]))),
                tenant: this.http
                    .get<Partial<Billing>>(this.api + '/tenants', { context: suppressErrorToast() })
                    .pipe(catchError(() => of(null))),
            }).pipe(
                map(({ roles, tenant }) => {
                    this.billingUnavailable.set(!tenant);
                    this.billing.set({ ...this.billing(), ...tenant, ...this.read(this.tenantKey + ':billing', {}) });
                    const admin = roles.some(
                        (role: { id: string; name: string }) =>
                            role.id === roleId && role.name.toLowerCase() === 'admin'
                    );
                    this.canCustomize.set(admin);
                    return admin;
                })
            );
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    constructor() {
        this.apply();
        let scheme = 'auto';
        const media = this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)');
        const update = () => {
            this.dark = scheme === 'dark' || (scheme === 'auto' && !!media?.matches);
            this.apply();
        };
        this.config.config$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
            scheme = config.scheme;
            update();
        });
        media?.addEventListener('change', update);
        this.destroyRef.onDestroy(() => media?.removeEventListener('change', update));
        this.access$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
    private read<T>(key: string, fallback: T): T {
        try {
            return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback;
        } catch {
            return fallback;
        }
    }
    private store(key: string, value: unknown) {
        localStorage.setItem(key, JSON.stringify(value));
    }
    setMode(mode: 'light' | 'dark') {
        void this.preferences.setMode(mode);
    }
    save(value: Appearance) {
        if (!this.canCustomize()) throw Error('Nur Administratoren können diese Einstellungen ändern.');
        const normalized = normalizeAppearance(value);
        this.store(this.tenantKey, normalized);
        this.appearance.set(normalized);
        this.apply();
    }
    selectScheme(scheme: BrandScheme) {
        const current = this.appearance();
        this.save({ ...current, scheme, color: current.colors[scheme] });
    }
    selectMenu(menuHighlight: 'border' | 'filled') {
        this.save({ ...this.appearance(), menuHighlight });
    }
    saveBilling(value: Billing) {
        if (!this.canCustomize()) throw Error('Keine Berechtigung.');
        const companyName = value.companyName.trim();
        if (!companyName) throw Error('Bitte einen Firmennamen angeben.');
        const next = { ...value, companyName };
        this.store(this.tenantKey + ':billing', next);
        this.billing.set(next);
    }
    private apply() {
        const a = this.appearance(),
            style = this.document.body.style;
        this.document.body.dataset['mzeitScheme'] = a.scheme;
        this.document.body.dataset['mzeitMenuHighlight'] = a.menuHighlight;
        const favicon = this.document.getElementById('mzeit-favicon');
        const faviconHref = appearanceFavicon(a.color);
        if (favicon && favicon.getAttribute('href') !== faviconHref) {
            favicon.setAttribute('href', faviconHref);
        }
        const values: Record<string, string> = {
            '--mzeit-accent': a.color,
            '--mzeit-on-accent': inkOn(a.color),
            '--mzeit-accent-ink': this.dark ? '#f1f5f9' : contrastInk(a.color, false),
            '--mzeit-nav': a.scheme === 'classic' ? '#172638' : '#262625',
        };
        const levels: Record<string, string> = {
            '50': mix(a.color, 255, 0.94),
            '100': mix(a.color, 255, 0.85),
            '200': mix(a.color, 255, 0.65),
            '300': mix(a.color, 255, 0.45),
            '400': mix(a.color, 255, 0.2),
            '500': a.color,
            '600': mix(a.color, 0, 0.12),
            '700': mix(a.color, 0, 0.25),
            '800': mix(a.color, 0, 0.4),
            '900': mix(a.color, 0, 0.6),
            '950': mix(a.color, 0, 0.75),
            '': a.color,
        };
        for (const [level, color] of Object.entries(levels)) {
            const suffix = level ? '-' + level : '';
            values['--fuse-primary' + suffix] = color;
            values['--fuse-primary' + suffix + '-rgb'] = rgb(color).join(',');
            values['--fuse-on-primary' + suffix] = inkOn(color);
            values['--fuse-on-primary' + suffix + '-rgb'] = rgb(inkOn(color)).join(',');
        }
        for (const [key, value] of Object.entries(values)) style.setProperty(key, value);
        try {
            this.store('mzeit.brand.boot.v1', { scheme: a.scheme, color: a.color });
        } catch {}
    }
}
